// The custom-download options on a phone, laid out like the photo edit drawer (components/common/PhotoEditDrawer): one row of tabs over
// one swipeable row of options, with the download button under them, so the photo keeps the height. The page owns all the state.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAdjustments, IconBorderOuter, IconCrop, IconDownload, IconLoader2, IconPhoto, IconPlus } from '@tabler/icons-react';
import { RoundTool } from '@/components/common/PhotoEditDrawer';
import { cn } from '@/lib/utils';
import { useDragWheelScroll } from '../../../lib/hooks';

const TABS = [
    { key: 'size', Icon: IconPhoto, labelKey: 'photoSizes' },
    { key: 'ratio', Icon: IconCrop, labelKey: 'cropRatio' },
    { key: 'quality', Icon: IconAdjustments, labelKey: 'imageQuality' },
    { key: 'border', Icon: IconBorderOuter, labelKey: 'border' },
];
const UNITS = ['px', 'mm', 'cm', 'in'];
const STRIP_CLS = '-mx-2 overflow-x-auto px-2 [scrollbar-width:none]';
const NUM_CLS = 'w-full min-w-0 rounded-lg border border-white/15 bg-white/10 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-primary/40';

function Pill({ on, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={on}
            className={cn('h-8 shrink-0 whitespace-nowrap rounded-full px-3.5 text-xs font-semibold transition-colors', on ? 'bg-primary text-white shadow-md shadow-black/30' : 'bg-white/10 text-white/80')}
        >
            {children}
        </button>
    );
}

export default function DownloadOptionsDock({
    sizes, sizeIcons, selectedPreset, onPreset,
    visaExpanded, onVisaToggle, visaGroups, selectedVisaGroup, onVisaGroup,
    customExpanded, onCustomToggle, customWidth, customHeight, onCustomWidth, onCustomHeight, unit, onUnit, toUnit,
    ratios, cropRatio, onRatio,
    qualities, imageQuality, onQuality,
    borderEnabled, onBorderEnabled, borderColor, onBorderColor, swatches, borderPct, onBorderPct, borderPx,
    onDownload, downloading = false,
}) {
    const { t } = useTranslation();
    const [tab, setTab] = useState('size');
    const tabStripRef = useDragWheelScroll();
    const sizeStripRef = useDragWheelScroll();
    const visaStripRef = useDragWheelScroll();
    const ratioStripRef = useDragWheelScroll();
    const borderStripRef = useDragWheelScroll();

    return (
        <div className="shrink-0 bg-zinc-950 px-2 pb-2 pt-1.5 text-white">
            <div ref={tabStripRef} className={STRIP_CLS}>
                <div className="mx-auto flex w-max gap-0.5">
                    {TABS.map((x) => (
                        <button
                            key={x.key}
                            type="button"
                            onClick={() => {
                                if (x.key === 'border' && tab !== 'border') onBorderEnabled(true); // opening the tab means they want a border
                                setTab(x.key);
                            }}
                            aria-pressed={tab === x.key}
                            className={cn('inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-semibold transition-colors', tab === x.key ? 'bg-primary text-white shadow-md shadow-black/30' : 'text-white/75')}
                        >
                            <x.Icon size={14} />
                            {t(`imageGeneration.${x.labelKey}`)}
                        </button>
                    ))}
                </div>
            </div>

            {/* The tab's options — a fixed minimum height, so switching tabs doesn't move the photo */}
            <div className="flex min-h-[64px] flex-col justify-center gap-1.5 py-1.5">
                {tab === 'size' && (
                    <>
                        {visaExpanded && (
                            <div ref={visaStripRef} className={STRIP_CLS}>
                                <div className="flex w-max gap-1.5">
                                    {visaGroups.map((group) => (
                                        <Pill key={group.key} on={selectedVisaGroup === group.key} onClick={() => onVisaGroup(group)}>{group.size}</Pill>
                                    ))}
                                </div>
                            </div>
                        )}
                        {customExpanded && (
                            <div className="flex items-end gap-1.5">
                                <input type="number" value={toUnit(customWidth)} onChange={(e) => onCustomWidth(Number(e.target.value))} aria-label={t('imageGeneration.widthPx')} className={NUM_CLS} />
                                <span className="pb-1.5 !text-xs !text-white/50">×</span>
                                <input type="number" value={toUnit(customHeight)} onChange={(e) => onCustomHeight(Number(e.target.value))} aria-label={t('imageGeneration.heightPx')} className={NUM_CLS} />
                                <div className="flex shrink-0 gap-0.5 rounded-lg bg-white/10 p-0.5">
                                    {UNITS.map((u) => (
                                        <button key={u} type="button" onClick={() => onUnit(u)} aria-pressed={unit === u} className={cn('h-7 w-7 rounded-md text-[11px] font-semibold', unit === u ? 'bg-primary text-white' : 'text-white/70')}>{u}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div ref={sizeStripRef} className={STRIP_CLS}>
                            <div className="mx-auto flex w-max gap-0.5">
                                {sizes.map((preset) => (
                                    <RoundTool
                                        key={preset.key}
                                        Icon={sizeIcons[preset.key] ?? IconPhoto}
                                        label={t(`imageGeneration.${preset.labelKey}`)}
                                        on={selectedPreset === preset.key}
                                        onClick={() => (preset.isVisa ? onVisaToggle() : onPreset(preset))}
                                    />
                                ))}
                                <RoundTool Icon={IconPlus} label={t('imageGeneration.abbrCustom') || 'Custom'} on={selectedPreset === 'custom'} onClick={onCustomToggle} />
                            </div>
                        </div>
                    </>
                )}

                {tab === 'ratio' && (
                    <div ref={ratioStripRef} className={STRIP_CLS}>
                        <div className="mx-auto flex w-max gap-1.5">
                            {ratios.map((r) => (
                                <Pill key={r.labelKey ?? r.label} on={(!selectedPreset || selectedPreset === 'custom') && cropRatio === r.value} onClick={() => onRatio(r.value)}>
                                    {r.labelKey ? t(`imageGeneration.${r.labelKey}`) : r.label}
                                </Pill>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 'quality' && (
                    <div className="flex justify-center gap-1.5">
                        {qualities.map((q) => (
                            <Pill key={q.key} on={imageQuality === q.value} onClick={() => onQuality(q.value)}>
                                {t(`imageGeneration.${q.key === 'low' ? 'qualityLow' : q.key === 'med' ? 'qualityMed' : 'qualityHigh'}`)}
                            </Pill>
                        ))}
                    </div>
                )}

                {tab === 'border' && (
                    <>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={borderEnabled}
                                aria-label={t('imageGeneration.border')}
                                onClick={() => onBorderEnabled(!borderEnabled)}
                                className={cn('relative h-5 w-9 shrink-0 rounded-full transition-colors', borderEnabled ? 'bg-primary' : 'bg-white/25')}
                            >
                                <span className={cn('absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform', borderEnabled && 'translate-x-4')} />
                            </button>
                            <div ref={borderStripRef} className={cn('min-w-0 flex-1 overflow-x-auto [scrollbar-width:none]', !borderEnabled && 'pointer-events-none opacity-40')}>
                                <div className="flex w-max items-center gap-2 p-1">
                                    {swatches.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => onBorderColor(c)}
                                            aria-label={c}
                                            aria-pressed={borderColor.toLowerCase() === c.toLowerCase()}
                                            className={cn('h-7 w-7 shrink-0 rounded-full border border-white/25', borderColor.toLowerCase() === c.toLowerCase() && 'ring-2 ring-primary ring-offset-2 ring-offset-zinc-950')}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                    <label aria-label={t('imageGeneration.borderColor')} className="relative flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-dashed border-white/40" style={swatches.includes(borderColor.toLowerCase()) ? undefined : { backgroundColor: borderColor }}>
                                        {swatches.includes(borderColor.toLowerCase()) && <IconPlus size={13} className="text-white/70" />}
                                        <input type="color" value={borderColor} onChange={(e) => onBorderColor(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
                                    </label>
                                </div>
                            </div>
                        </div>
                        {borderEnabled && (
                            <div className="flex items-center gap-2">
                                <span className="shrink-0 !text-[10px] !text-white/60">{t('imageGeneration.borderThickness')}</span>
                                <input type="range" value={borderPct} onChange={(e) => onBorderPct(Number(e.target.value))} min={0.5} max={15} step={0.5} className="h-1 min-w-0 flex-1 accent-primary" />
                                <span className="w-12 shrink-0 text-right font-mono !text-[10px] !text-white/60">{borderPx}px</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            <button
                type="button"
                onClick={onDownload}
                disabled={downloading}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
            >
                {downloading ? <IconLoader2 size={18} className="animate-spin" /> : <IconDownload size={18} />}
                {t(downloading ? 'imageGeneration.downloading' : 'imageGeneration.downloadCustom')}
            </button>
        </div>
    );
}
